/**
 * Live targets: render target textures a pass redraws, which every reader shares as Godot's readers
 * share a ViewportTexture RID. Each Godot reader samples that RID through its own sampler, so a
 * reader whose sampler differs from the target's reads a copy of its own, which the pass refills
 * after each render. A crop of a live target is a copy too, and every copy is live in its turn.
 */
import * as THREE from 'three';
import { copySampling } from '../resources/textures/copySampling';
import { imageSize, readRenderTargetPixels } from '../resources/formats/image/imagePixels';
import { perObject } from '../utils/perObject';
import { drawOffscreen } from './offscreen';
import { isHeld } from './useDisposeOnRelease';
import { holdsSrgbBytes, markSrgbBytes } from './srgbByteTextures';
import { copyTexels, wholeTexels } from './texelCopy';
/**
 * Written by `publishLiveTarget`, its withdrawal, `addCopy` and `noteLiveTargetDrawn`. An entry
 * goes with its texture, and a copy leaves `copies` when it is disposed: by its last holder, or by
 * its target's withdrawal when no reader holds it.
 */
const liveTargetOf = perObject(() => ({
    publishers: 0,
    copies: new Set(),
    renderer: null,
    isTransparent: false,
    draws: 0,
    root: null,
}));
/**
 * Marks each of `textures` live while `renderer` redraws it, and answers one withdrawal for all. Two
 * publishers of one texture keep it live until both withdraw. `isTransparent` is the viewport's
 * `transparent_bg`.
 */
export function publishLiveTargets(textures, renderer, isTransparent) {
    const withdrawals = textures.map((texture) => publishLiveTarget(texture, renderer, isTransparent));
    return () => withdrawals.forEach((withdraw) => withdraw());
}
function publishLiveTarget(texture, renderer, isTransparent) {
    const live = liveTargetOf(texture);
    live.publishers++;
    setPublisher(texture, renderer, isTransparent);
    let isWithdrawn = false;
    return () => {
        if (isWithdrawn)
            return;
        isWithdrawn = true;
        live.publishers--;
        // A microtask, not now: a target that publishes again in one commit keeps the copies a reader
        // made in that render and holds only after it.
        queueMicrotask(() => freeUnheldCopies(live));
    };
}
/** Gives `texture` and every copy of it, at any depth, the renderer and transparency of its publisher. */
function setPublisher(texture, renderer, isTransparent) {
    const live = liveTargetOf(texture);
    live.renderer = renderer;
    live.isTransparent = isTransparent;
    for (const copy of live.copies)
        setPublisher(copy.texture, renderer, isTransparent);
}
/** Frees the copies of a target no pass publishes any more, which no reader holds and none will. */
function freeUnheldCopies(live) {
    if (live.publishers > 0)
        return;
    for (const copy of [...live.copies]) {
        if (!isHeld(copy.texture))
            copy.texture.dispose();
    }
}
/** Counts a draw of `texture`. A target's pass calls it after each render. */
export function noteLiveTargetDrawn(texture) {
    liveTargetOf(texture).draws++;
}
/**
 * How many times the pass has drawn `texture`. A reader that copies the target once, as a sky bake
 * does, waits for the first draw and copies again on each later one.
 */
export function liveTargetDrawCount(texture) {
    const live = liveTargetOf(texture);
    return (live.root ?? live).draws;
}
/** Whether a pass redraws `texture`, so a reader that samples it differently needs its own copy. */
export function isLiveTarget(texture) {
    return texture.isRenderTargetTexture === true && liveTargetOf(texture).publishers > 0;
}
/**
 * The texel Godot reads from the image of a live target no pass has rendered yet: zero, with no
 * alpha channel in an opaque target's image (`renderer_rd/storage_rd/texture_storage.cpp:3571-3575`).
 */
export function unrenderedTexel(texture) {
    return liveTargetOf(texture).isTransparent ? [0, 0, 0, 0] : [0, 0, 0, 255];
}
/**
 * The bytes Godot's image of a live target holds now, bottom row first, read back from its
 * renderer. Undefined for a texture no renderer publishes. A GPU readback, so only a rebuild that copies the target reads it.
 */
export function liveTargetPixels(texture) {
    const { renderer, publishers, isTransparent } = liveTargetOf(texture);
    if (!renderer || publishers === 0 || !texture.renderTarget)
        return undefined;
    const pixels = readRenderTargetPixels(renderer, texture.renderTarget);
    if (!isTransparent)
        dropAlpha(pixels.data);
    return pixels;
}
/** An opaque target's image has no alpha channel (`renderer_rd/storage_rd/texture_storage.cpp:3571-3575`). */
function dropAlpha(data) {
    for (let alpha = 3; alpha < data.length; alpha += 4)
        data[alpha] = 255;
}
/** Whether any copy of `texture` is live, so its pass has a copy to refill after a render. */
export function hasLiveCopies(texture) {
    return liveTargetOf(texture).copies.size > 0;
}
/**
 * Refills every copy of `texture` that a reader holds, then the copies of each copy. The target's
 * pass calls it after each render, inside its own offscreen draw. A copy no reader holds comes from
 * a render React discarded, or from a reader that has not committed yet and holds it before the next
 * render.
 */
export function refillLiveCopies(texture, gl) {
    fillCopies(texture, gl, (copy) => isHeld(copy.texture));
}
/**
 * Fills `copy` of `source` now, for a reader that samples it before the target's next render, as a
 * sky bake does. A texture that is no copy of `source` needs no fill.
 */
export function fillLiveCopy(source, copy, gl) {
    const live = [...liveTargetOf(source).copies].find((candidate) => candidate.texture === copy);
    if (live)
        drawOffscreen(gl, () => live.fill(gl));
}
/** Fills the copies `isFilled` picks, at every depth. The caller draws offscreen. */
function fillCopies(texture, gl, isFilled) {
    for (const copy of liveTargetOf(texture).copies) {
        if (!isFilled(copy))
            continue;
        copy.fill(gl);
        fillCopies(copy.texture, gl, isFilled);
    }
}
/**
 * A copy of `source`'s storage and sampler, of the whole or of the region `layout` cuts, which the
 * caller samples through its own sampler and frees with `dispose()`.
 */
export function liveTargetCopy(source, layout) {
    const { width, height, placement } = copyRegion(source, layout);
    return addCopy(source, width, height, placement, viewSharing(width, height, placement));
}
/** The copies `liveTargetCopy` made of the same region of `source`, not freed, in the order they were made. */
export function liveTargetCopies(source, layout) {
    const { width, height, placement } = copyRegion(source, layout);
    const sharing = viewSharing(width, height, placement);
    return [...liveTargetOf(source).copies]
        .filter((copy) => copy.sharing === sharing)
        .map((copy) => copy.texture);
}
/**
 * The region `layout` cuts out of `source`, as Godot's AtlasTexture draws it: the region at its
 * margin offset inside the reported box, transparent elsewhere. Readers that cut one region share
 * one crop, as Godot's AtlasTextures draw from one RID. Each reader holds it, and the last to let go
 * frees it.
 */
export function liveTargetCrop(source, layout) {
    const { width, height, placement } = copyRegion(source, layout);
    const sharing = `crop ${regionKey(width, height, placement)}`;
    const shared = [...liveTargetOf(source).copies].find((copy) => copy.sharing === sharing);
    return shared?.texture ?? addCopy(source, width, height, placement, sharing);
}
function viewSharing(width, height, placement) {
    return `view ${regionKey(width, height, placement)}`;
}
function regionKey(width, height, placement) {
    return JSON.stringify([width, height, placement]);
}
/** The size and placement of a copy of `source`: the region `layout` cuts, or the whole. */
function copyRegion(source, layout) {
    const size = targetSize(source);
    if (!layout)
        return { ...size, placement: wholeTexels(size.width, size.height) };
    return { width: layout.width, height: layout.height, placement: cropPlacement(size.height, layout) };
}
/**
 * The placement that cuts `layout` out of a source `sourceHeight` texels high. A layout counts rows
 * from the top, as Godot's images do, and a render target from the bottom.
 */
function cropPlacement(sourceHeight, { height, source, dest }) {
    const destX = Math.round(dest.x);
    const destY = Math.round(dest.y);
    return {
        offsetX: source.x - destX,
        offsetY: sourceHeight - height - source.y + destY,
        rowStep: 1,
        bounds: [
            source.x,
            sourceHeight - source.y - source.height,
            source.x + source.width,
            sourceHeight - source.y,
        ],
    };
}
/**
 * A `width` x `height` copy of `source`'s storage that `source`'s pass fills through `placement`.
 * The copy keeps the source's format, tag and sRGB-byte mark, so it samples the bytes the source
 * holds the way the source would. It is live, so a reader of the copy is filled after it.
 */
function addCopy(source, width, height, placement, sharing) {
    const target = new THREE.WebGLRenderTarget(width, height, { depthBuffer: false, type: source.type });
    const texture = copySampling(target.texture, source);
    texture.name = `${source.name}::copy`;
    texture.internalFormat = source.internalFormat;
    texture.colorSpace = source.colorSpace;
    if (holdsSrgbBytes(source))
        markSrgbBytes(texture);
    const sourceLive = liveTargetOf(source);
    const { copies, renderer, isTransparent } = sourceLive;
    const copy = {
        texture,
        sharing,
        fill: (gl) => copyTexels(gl, source, target, placement),
    };
    copies.add(copy);
    const withdraw = publishLiveTarget(texture, renderer, isTransparent);
    liveTargetOf(texture).root = sourceLive.root ?? sourceLive;
    texture.addEventListener('dispose', () => {
        withdraw();
        copies.delete(copy);
        target.dispose();
    });
    return texture;
}
/** A render target's size, which its `image` always reports. */
function targetSize(texture) {
    return imageSize(texture.image) ?? { width: 0, height: 0 };
}
//# sourceMappingURL=liveTargets.js.map